import React, { useState, useEffect, useRef } from 'react';
import { Database, Edit, Trash2, Save, X, AlertTriangle, RefreshCw, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const AdminDados = () => {
    const [tabelas,          setTabelas]          = useState([]);
    const [tabelaSelecionada, setTabelaSelecionada] = useState('');
    const [colunas,          setColunas]          = useState([]);   // SHOW COLUMNS result
    const [dados,            setDados]            = useState([]);
    const [loading,          setLoading]          = useState(false);
    const [editingKey,       setEditingKey]       = useState(null);
    const [editFormData,     setEditFormData]     = useState({});
    const [filtrosAtivos,    setFiltrosAtivos]    = useState({});
    const [currentPage,      setCurrentPage]      = useState(1);
    const [selectedKeys,     setSelectedKeys]     = useState([]);

    // Combobox state
    const [buscaTabela,      setBuscaTabela]      = useState('');
    const [dropdownAberto,   setDropdownAberto]   = useState(false);
    const comboboxRef = useRef(null);

    // Seletor de colunas para filtros
    const [colunasAtivas,     setColunasAtivas]     = useState([]);  // campos com filtro ativo
    const [seletorAberto,     setSeletorAberto]     = useState(false);
    const seletorRef = useRef(null);

    const itemsPerPage = 50;

    const token = localStorage.getItem('authToken');
    const authHeader = { 'Authorization': `Bearer ${token}` };

    // Contador: só aplica resultado do fetch mais recente (descarta respostas tardias)
    const fetchCountRef = useRef(0);
    // Debounce para inputs de texto: evita fetch a cada tecla
    const debounceRef = useRef(null);

    // Fecha dropdowns ao clicar fora
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (comboboxRef.current && !comboboxRef.current.contains(e.target))
                setDropdownAberto(false);
            if (seletorRef.current && !seletorRef.current.contains(e.target))
                setSeletorAberto(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Reseta colunas ativas ao trocar de tabela
    useEffect(() => {
        setColunasAtivas([]);
        setFiltrosAtivos({});
    }, [tabelaSelecionada]);

    // Busca lista real de tabelas do banco
    useEffect(() => {
        const token = localStorage.getItem('authToken');
        fetch('/api/dados/tabelas', { headers: { 'Authorization': `Bearer ${token}` } })
            .then(async r => {
                if (!r.ok) {
                    const body = await r.text();
                    console.error('[AdminDados] Resposta 403 body:', body);
                    throw new Error(`HTTP ${r.status}`);
                }
                return r.json();
            })
            .then(lista => {
                if (Array.isArray(lista) && lista.length > 0) {
                    setTabelas(lista);
                    setTabelaSelecionada(lista[0]);
                }
            })
            .catch(e => console.error('[AdminDados] Erro ao buscar tabelas:', e));
    }, []); // eslint-disable-line

    // Busca colunas para filtros dinâmicos quando muda a tabela
    useEffect(() => {
        if (!tabelaSelecionada) return;
        fetch(`/api/dados/${tabelaSelecionada}/colunas`, { headers: authHeader })
            .then(r => r.json())
            .then(cols => setColunas(Array.isArray(cols) ? cols : []))
            .catch(() => setColunas([]));
    }, [tabelaSelecionada]); // eslint-disable-line

    // Colunas filtráveis: exclui PKs, hashes, textos longos e campos sensíveis
    const colunasFiltravelis = colunas.filter(col => {
        const f = col.Field.toLowerCase();
        const t = col.Type.toLowerCase();
        if (col.Key === 'PRI') return false;
        if (col.Extra?.includes('auto_increment')) return false;
        if (['text','longtext','mediumtext','blob','mediumblob'].some(tp => t.startsWith(tp))) return false;
        if (['senha','hash','token','password','secret'].some(s => f.includes(s))) return false;
        return true;
    }).slice(0, 7);

    const getPrimaryKey = (tabela) => {
        if (tabela === 'dtbConfere') return 'ID_FUNCIONARIO';
        if (tabela === 'dtbListaEmail') return 'CPF';
        if (tabela === 'dtbAtestadosMedicos') return 'id';
        return 'ID'; 
    };
    const pk = getPrimaryKey(tabelaSelecionada);

    const LIMITE_PAGINA = 50;
    const MAX_REGISTROS = 10000;
    const [totalRegistros, setTotalRegistros] = useState(0);

    const fetchData = async (pagina = 1, filtrosOverride = null) => {
        if (!tabelaSelecionada) return;
        const offset = (pagina - 1) * LIMITE_PAGINA;

        // Incrementa contador — só o fetch mais recente aplica seus resultados
        const myCount = ++fetchCountRef.current;

        setLoading(true);
        setEditingKey(null);
        setSelectedKeys([]);
        try {
            const filtrosAtuais = filtrosOverride ?? filtrosAtivos;
            const params = new URLSearchParams({ limite: LIMITE_PAGINA, offset });
            Object.entries(filtrosAtuais).forEach(([col, val]) => {
                if (val) params.append(`f_${col}`, val);
            });
            const response = await fetch(
                `/api/dados/${tabelaSelecionada}?${params.toString()}`,
                { headers: authHeader }
            );
            const result = await response.json();

            // Descarta resposta se já existe um fetch mais recente
            if (myCount !== fetchCountRef.current) return;

            if (result.rows) {
                setDados(result.rows);
                setTotalRegistros(result.total || 0);
            } else if (Array.isArray(result)) {
                setDados(result);
                setTotalRegistros(result.length);
            } else {
                alert("Erro: " + (result.message || "Falha desconhecida"));
                setDados([]);
            }
        } catch (error) {
            console.error(error);
            alert("Erro de conexão.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setFiltrosAtivos({});
        setCurrentPage(1);
        setSelectedKeys([]);
        fetchData();
    }, [tabelaSelecionada]);

    useEffect(() => {
        setSelectedKeys([]);
    }, [currentPage, filtrosAtivos]);

    const handleFilterChange = (coluna, valor, debounce = false) => {
        const novos = { ...filtrosAtivos, [coluna]: valor };
        setFiltrosAtivos(novos);
        setCurrentPage(1);
        if (debounce) {
            // Texto: aguarda 400ms após parar de digitar antes de buscar
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => fetchData(1, novos), 400);
        } else {
            fetchData(1, novos);
        }
    };

    const isDateColumn = (coluna) => coluna.toUpperCase().includes('DATA');

    const getUniqueValues = (coluna) => {
        if (!dados || dados.length === 0) return [];
        const values = dados
            .map(row => row[coluna])
            .filter(val => val !== null && val !== undefined && val !== '');
        
        return [...new Set(values)].sort(); 
    };

    // Filtros agora são server-side — dados já chegam filtrados
    const dadosFiltrados = dados;

    // Paginação server-side: totalPages baseado no total real do banco (cap 10k)
    const totalEfetivo = Math.min(totalRegistros, MAX_REGISTROS);
    const totalPages   = Math.max(1, Math.ceil(totalEfetivo / LIMITE_PAGINA));
    const currentItems = dadosFiltrados; // server já retorna só 50 por vez

    const handleBulkDelete = async () => {
        if (!window.confirm(`ATENÇÃO: Tem a certeza que deseja excluir ${selectedKeys.length} registos selecionados? Esta ação é irreversível.`)) return;
        
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`/api/dados/${tabelaSelecionada}/bulk-delete`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ ids: selectedKeys }) 
            });
            
            if (response.ok) {
                alert("Registos excluídos com sucesso!");
                fetchData(); 
            } else {
                const err = await response.json();
                alert("Erro ao excluir: " + err.message);
            }
        } catch (error) {
            alert("Erro de conexão ao excluir.");
        }
    };

    const handleEditClick = (row) => {
        setEditingKey(row[pk]);
        setEditFormData({ ...row });
    };

    const handleCancelClick = () => {
        setEditingKey(null);
        setEditFormData({});
    };

    const handleEditFormChange = (event) => {
        const fieldName = event.target.getAttribute("name");
        const fieldValue = event.target.value;
        setEditFormData({ ...editFormData, [fieldName]: fieldValue });
    };

    const handleSaveClick = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`/api/dados/${tabelaSelecionada}/${editingKey}`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(editFormData)
            });
            
            if (response.ok) {
                alert("Salvo com sucesso!");
                setEditingKey(null);
                fetchData();
            } else {
                const err = await response.json();
                alert("Erro ao salvar: " + err.message);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleDeleteClick = async (keyValue) => {
        if (!window.confirm(`ATENÇÃO: Tem a certeza que deseja excluir o registo ${keyValue}?`)) return;
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`/api/dados/${tabelaSelecionada}/${keyValue}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) fetchData();
        } catch (error) {
            console.error(error);
        }
    };

    const formatarValor = (valor) => {
        if (valor === null || valor === undefined) return null;

        const str = String(valor);

        // Datas
        if (/^\d{4}-\d{2}-\d{2}(T|\s|$)/.test(str)) {
            const tz = 'America/Sao_Paulo';

            // TIMESTAMP do MySQL: vem como UTC com Z → converte para horário de Brasília
            if (str.includes('T') && str.endsWith('Z')) {
                const d = new Date(str);
                const hora  = d.toLocaleTimeString('pt-BR',  { timeZone: tz, hour: '2-digit', minute: '2-digit' });
                const data  = d.toLocaleDateString('pt-BR',  { timeZone: tz, day: '2-digit', month: '2-digit', year: 'numeric' });
                // Se hora for meia-noite é coluna DATE (artefato de timezone) → mostra só data
                return (hora === '00:00' || hora === '0:00') ? data : `${data} ${hora}`;
            }

            // DATETIME do MySQL: vem como string "YYYY-MM-DD HH:MM:SS" — sem conversão
            if (str.includes(' ') && str.length > 10) {
                const [ano, mes, dia] = str.substring(0, 10).split('-');
                return `${dia}/${mes}/${ano} ${str.substring(11, 16)}`;
            }

            // DATE pura: "YYYY-MM-DD"
            const [ano, mes, dia] = str.split('-');
            return `${dia}/${mes}/${ano}`;
        }

        // Decimais como número JS (float)
        if (typeof valor === 'number' && !Number.isInteger(valor)) {
            return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }

        // Decimais vindos como string do MySQL (DECIMAL/FLOAT → "1.650000")
        if (/^-?\d+\.\d+$/.test(str)) {
            const num = parseFloat(str);
            if (!isNaN(num)) {
                return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            }
        }

        return str;
    };

    const podeEditar  = tabelaSelecionada === 'dtbConfere';
    const colorTheme  = '#10b981';
    const bgThemeLight = '#ecfdf5';

    const tabelasFiltradas = tabelas.filter(t =>
        t.toLowerCase().includes(buscaTabela.toLowerCase())
    );

    return (
        <div>
            {/* CABEÇALHO DO MÓDULO */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{ padding: '12px', backgroundColor: bgThemeLight, borderRadius: '12px', color: colorTheme }}>
                        <Database size={28} />
                    </div>
                    <div>
                        <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '0 0 4px 0' }}>Editor de Dados (SQL)</h2>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            {/* COMBOBOX PESQUISÁVEL */}
                            <div ref={comboboxRef} style={{ position: 'relative' }}>
                                <input
                                    value={dropdownAberto ? buscaTabela : tabelaSelecionada}
                                    onChange={e => { setBuscaTabela(e.target.value); setDropdownAberto(true); }}
                                    onClick={() => { setBuscaTabela(''); setDropdownAberto(true); }}
                                    placeholder="Pesquisar tabela..."
                                    readOnly={!dropdownAberto}
                                    style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', fontWeight: '600', color: '#334155', width: '240px', cursor: 'pointer' }}
                                />
                                {dropdownAberto && (
                                    <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 50, backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: '260px', overflowY: 'auto', width: '260px', marginTop: '4px' }}>
                                        {tabelasFiltradas.length === 0 ? (
                                            <div style={{ padding: '14px', fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>Nenhuma tabela encontrada</div>
                                        ) : tabelasFiltradas.map(t => (
                                            <div
                                                key={t}
                                                onClick={() => { setTabelaSelecionada(t); setBuscaTabela(''); setDropdownAberto(false); setFiltrosAtivos({}); }}
                                                style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: t === tabelaSelecionada ? '700' : '500', backgroundColor: t === tabelaSelecionada ? '#ecfdf5' : 'transparent', color: t === tabelaSelecionada ? colorTheme : '#334155', borderBottom: '1px solid #f1f5f9' }}
                                                onMouseOver={e => { if (t !== tabelaSelecionada) e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                                                onMouseOut={e  => { if (t !== tabelaSelecionada) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                            >
                                                {t}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: '700', backgroundColor: '#fff7ed', color: '#ea580c', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ffedd5', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <AlertTriangle size={12} /> PK: {pk}
                            </span>
                            {totalRegistros > 0 && (
                                <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', padding: '4px 8px', borderRadius: '4px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                    {dados.length < totalRegistros
                                        ? `Exibindo ${dados.length.toLocaleString('pt-BR')} de ${totalRegistros.toLocaleString('pt-BR')} registros`
                                        : `${totalRegistros.toLocaleString('pt-BR')} registros`
                                    }
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {podeEditar && selectedKeys.length > 0 && (
                        <button 
                            onClick={handleBulkDelete}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: 'background-color 0.2s' }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
                        >
                            <Trash2 size={16} /> Excluir ({selectedKeys.length})
                        </button>
                    )}
                    <button 
                        onClick={() => fetchData(currentPage)}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e2e8f0'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                    >
                        <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Atualizar
                    </button>
                </div>
            </div>

            {/* SELETOR DE FILTROS */}
            {colunasFiltravelis.length > 0 && (
                <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>

                    {/* Botão que abre o seletor de colunas */}
                    <div ref={seletorRef} style={{ position: 'relative' }}>
                        <button
                            onClick={() => setSeletorAberto(v => !v)}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: colunasAtivas.length > 0 ? '#ecfdf5' : '#f8fafc', color: colunasAtivas.length > 0 ? colorTheme : '#475569', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}
                        >
                            <Search size={14} />
                            Filtros {colunasAtivas.length > 0 && `(${colunasAtivas.length})`}
                        </button>

                        {seletorAberto && (
                            <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 40, backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.10)', padding: '12px', marginTop: '4px', minWidth: '220px', maxHeight: '300px', overflowY: 'auto' }}>
                                <p style={{ margin: '0 0 10px 0', fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Selecione as colunas</p>
                                {colunas.map(col => (
                                    <label key={col.Field} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 4px', cursor: 'pointer', fontSize: '13px', color: '#334155', borderRadius: '4px' }}
                                        onMouseOver={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                        onMouseOut={e  => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={colunasAtivas.includes(col.Field)}
                                            onChange={e => {
                                                if (e.target.checked) {
                                                    setColunasAtivas(prev => [...prev, col.Field]);
                                                } else {
                                                    setColunasAtivas(prev => prev.filter(c => c !== col.Field));
                                                    setFiltrosAtivos(prev => { const n = {...prev}; delete n[col.Field]; return n; });
                                                }
                                            }}
                                            style={{ accentColor: colorTheme, width: '14px', height: '14px' }}
                                        />
                                        <span>{col.Field}</span>
                                        <span style={{ marginLeft: 'auto', fontSize: '10px', color: '#94a3b8' }}>{col.Type.split('(')[0]}</span>
                                    </label>
                                ))}
                                {colunasAtivas.length > 0 && (
                                    <button onClick={() => { setColunasAtivas([]); setFiltrosAtivos({}); }} style={{ marginTop: '10px', width: '100%', padding: '6px', borderRadius: '6px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc', color: '#64748b', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                                        Limpar seleção
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Inputs dos filtros selecionados */}
                    {colunasAtivas.map(field => {
                        const col  = colunas.find(c => c.Field === field);
                        if (!col) return null;
                        const tipo = col.Type.toLowerCase();
                        const isDate = tipo.startsWith('date') || tipo.startsWith('datetime');
                        const isEnum = tipo.startsWith('enum');
                        const enumOps = isEnum
                            ? tipo.replace(/^enum\(/, '').replace(/\)$/, '').split(',').map(v => v.replace(/'/g, '').trim())
                            : [];

                        const inputStyle = { padding: '4px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px', backgroundColor: '#fff', color: '#334155', minWidth: '160px' };

                        return (
                            <div key={field} style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '4px 10px 4px 8px' }}>
                                <span style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', whiteSpace: 'nowrap' }}>{field}</span>
                                {isDate ? (
                                    <input type="date" value={filtrosAtivos[field] || ''} onChange={e => handleFilterChange(field, e.target.value)} style={inputStyle} />
                                ) : isEnum ? (
                                    <select value={filtrosAtivos[field] || ''} onChange={e => handleFilterChange(field, e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                                        <option value="">Todos</option>
                                        {enumOps.map(v => <option key={v} value={v}>{v}</option>)}
                                    </select>
                                ) : (
                                    <input type="text" placeholder={`Pesquisar...`} value={filtrosAtivos[field] || ''} onChange={e => handleFilterChange(field, e.target.value, true)} style={inputStyle} />
                                )}
                            </div>
                        );
                    })}

                    {Object.values(filtrosAtivos).some(v => v) && (
                        <button
                            onClick={() => { setFiltrosAtivos({}); fetchData(1, {}); }}
                            style={{ padding: '5px 12px', borderRadius: '8px', border: '1px solid #fecdd3', backgroundColor: '#fff1f2', color: '#e11d48', fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                            Limpar filtros
                        </button>
                    )}
                </div>
            )}

            {/* BANNER DE LOADING — fixo no topo, sempre visível */}
            {loading && (
                <div style={{
                    position: 'sticky', top: 0, zIndex: 30,
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 20px', marginBottom: '12px',
                    backgroundColor: '#eff6ff', border: '1px solid #bfdbfe',
                    borderRadius: '10px', fontSize: '14px', fontWeight: '600', color: '#1d4ed8'
                }}>
                    <RefreshCw size={16} className="animate-spin" />
                    Buscando dados de <strong style={{ marginLeft: '2px' }}>{tabelaSelecionada}</strong>…
                    <span style={{ marginLeft: 'auto', fontSize: '12px', fontWeight: '400', color: '#3b82f6' }}>
                        Tabelas grandes podem levar alguns segundos
                    </span>
                </div>
            )}

            {/* AVISO DE CAP — discreto */}
            {totalRegistros >= MAX_REGISTROS && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', fontSize: '12px', color: '#92400e' }}>
                    <AlertTriangle size={13} color="#f59e0b" />
                    <span>Limite de 10.000 registros atingido — use filtros para refinar.</span>
                </div>
            )}

            {/* TABELA DINÂMICA MODERNIZADA */}
            <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto', maxHeight: '500px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '1000px' }}>
                        
                        <thead style={{ position: 'sticky', top: 0, zIndex: 20 }}>
                            <tr style={{ backgroundColor: '#f8fafc', color: '#64748b', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                
                                <th style={{ padding: '16px', fontWeight: '600', borderBottom: '1px solid #e2e8f0', position: 'sticky', left: 0, backgroundColor: '#f8fafc', zIndex: 30, borderRight: '1px solid #e2e8f0', minWidth: '120px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <input 
                                            type="checkbox" 
                                            style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: colorTheme }}
                                            checked={currentItems.length > 0 && selectedKeys.length === currentItems.length}
                                            onChange={(e) => {
                                                if (e.target.checked) setSelectedKeys(currentItems.map(row => row[pk]));
                                                else setSelectedKeys([]);
                                            }}
                                        />
                                        <span>Ações</span>
                                    </div>
                                </th>

                                {dados.length > 0 && Object.keys(dados[0]).map((key) => (
                                    <th key={key} style={{ padding: '16px 24px', fontWeight: '600', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', backgroundColor: key === pk ? bgThemeLight : '#f8fafc', color: key === pk ? colorTheme : '#64748b' }}>
                                        {key}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="100%" style={{ padding: '60px', textAlign: 'center', color: '#cbd5e1', fontSize: '13px' }}>—</td></tr>
                            ) : currentItems.length === 0 ? (
                                <tr><td colSpan="100%" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Nenhum registo encontrado.</td></tr>
                            ) : (
                                currentItems.map((row, index) => {
                                    const rowKey = row[pk]; 
                                    const isEditing = editingKey === rowKey;
                                    const isSelected = selectedKeys.includes(rowKey);

                                    return (
                                        <tr key={index} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: isEditing ? '#fefce8' : isSelected ? '#fef2f2' : 'transparent', transition: 'background-color 0.2s' }} onMouseOver={(e) => { if(!isEditing && !isSelected) e.currentTarget.style.backgroundColor = '#f8fafc' }} onMouseOut={(e) => { if(!isEditing && !isSelected) e.currentTarget.style.backgroundColor = 'transparent' }}>
                                            
                                            {/* COLUNA 1: AÇÕES */}
                                            <td style={{ padding: '12px 16px', position: 'sticky', left: 0, backgroundColor: isEditing ? '#fefce8' : isSelected ? '#fef2f2' : '#fff', zIndex: 10, borderRight: '1px solid #f1f5f9' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    {podeEditar && (
                                                        <input
                                                            type="checkbox"
                                                            style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: colorTheme }}
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                                if (e.target.checked) setSelectedKeys(prev => [...prev, rowKey]);
                                                                else setSelectedKeys(prev => prev.filter(k => k !== rowKey));
                                                            }}
                                                        />
                                                    )}

                                                    {podeEditar && (isEditing ? (
                                                        <div style={{ display: 'flex', gap: '6px' }}>
                                                            <button onClick={handleSaveClick} style={{ background: '#dcfce7', color: '#166534', border: 'none', padding: '6px', borderRadius: '4px', cursor: 'pointer' }} title="Salvar"><Save size={16} /></button>
                                                            <button onClick={handleCancelClick} style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '6px', borderRadius: '4px', cursor: 'pointer' }} title="Cancelar"><X size={16} /></button>
                                                        </div>
                                                    ) : (
                                                        <div style={{ display: 'flex', gap: '6px' }}>
                                                            <button onClick={() => handleEditClick(row)} style={{ background: 'none', color: '#3b82f6', border: 'none', cursor: 'pointer' }} title="Editar"><Edit size={16} /></button>
                                                            <button onClick={() => handleDeleteClick(rowKey)} style={{ background: 'none', color: '#ef4444', border: 'none', cursor: 'pointer' }} title="Excluir"><Trash2 size={16} /></button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>

                                            {/* DADOS */}
                                            {Object.keys(row).map((key) => (
                                                <td key={key} style={{ padding: '12px 24px', whiteSpace: 'nowrap', color: '#334155', fontSize: '14px' }}>
                                                    {isEditing ? (
                                                        <input
                                                            type="text"
                                                            name={key}
                                                            value={editFormData[key] === null ? '' : editFormData[key]}
                                                            onChange={handleEditFormChange}
                                                            disabled={key === pk} 
                                                            style={{ width: '100%', padding: '6px 10px', borderRadius: '4px', border: '1px solid #cbd5e1', outline: 'none', backgroundColor: key === pk ? '#f1f5f9' : '#fff', color: key === pk ? '#94a3b8' : '#0f172a' }}
                                                        />
                                                    ) : (
                                                        <span style={{ fontWeight: key === pk ? '700' : 'normal', fontFamily: key === pk ? 'monospace' : 'inherit' }}>
                                                            {row[key] !== null && row[key] !== undefined
                                                                ? formatarValor(row[key])
                                                                : <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>null</span>}
                                                        </span>
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
                
                {/* PAGINAÇÃO */}
                {!loading && dadosFiltrados.length > 0 && (
                    <div style={{ padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                        <span style={{ fontSize: '13px', color: '#64748b' }}>
                            Mostrando <span style={{ fontWeight: '700' }}>{((currentPage - 1) * LIMITE_PAGINA) + 1}</span> a <span style={{ fontWeight: '700' }}>{((currentPage - 1) * LIMITE_PAGINA) + dadosFiltrados.length}</span> de <span style={{ fontWeight: '700' }}>{totalEfetivo.toLocaleString('pt-BR')}</span> registros
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <button
                                onClick={() => { const p = Math.max(currentPage - 1, 1); setCurrentPage(p); fetchData(p); }}
                                disabled={currentPage === 1}
                                style={{ padding: '6px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: currentPage === 1 ? '#94a3b8' : '#0f172a', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}>
                                <ChevronLeft size={18} />
                            </button>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                                Página {currentPage} de {totalPages}
                                {totalRegistros > MAX_REGISTROS && <span style={{ color: '#94a3b8', fontWeight: '400' }}> (cap. 10k)</span>}
                            </span>
                            <button
                                onClick={() => { const p = Math.min(currentPage + 1, totalPages); setCurrentPage(p); fetchData(p); }}
                                disabled={currentPage === totalPages}
                                style={{ padding: '6px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: currentPage === totalPages ? '#94a3b8' : '#0f172a', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}>
                                <ChevronRight size={18} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminDados;